/**
 * MKS-349 — /invoices/import. Back-fill the invoice numbers that were issued
 * in the accounting system onto the Monksheets billing records.
 *
 * Four steps in one page, because they are one task and splitting them across
 * routes would lose the uploaded file on every navigation:
 *
 *   upload → map the columns → review the proposals → confirm
 *
 * The review step is the substance. It shows FOUR groups, which is what the
 * ticket asks for: confirmed-looking matches (tick list, "select all"),
 * ambiguous rows (pick the candidate), rows nothing matched, and the
 * Monksheets invoices nothing claimed — the last two side by side, with the
 * unmatched rows able to pair manually against any of those invoices.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import {
  useAnalyzeImportFile,
  useConfirmImportPairs,
  useMatchImportFile,
} from '../../hooks/useInvoiceImport';
import { formatCurrency, formatDisplayDate } from '../../utils/format';
import { describeRowChanges, formatChangePreview } from './invoiceImportDiff';
import {
  IMPORT_FIELDS,
  type AnalyzeResult,
  type ColumnMapping,
  type ConfirmPair,
  type ConfirmResult,
  type ImportField,
  type ImportInvoiceView,
  type MatchOptions,
  type MatchResult,
  type MatchRow,
} from '../../services/api/invoiceImport';

const FIELD_LABELS: Record<ImportField, string> = {
  invoiceNumber: 'Invoice number',
  clientName: 'Client name',
  clientId: 'Client ID / IČO',
  issueDate: 'Issue date',
  dueDate: 'Due date',
  netAmount: 'Amount without VAT',
  grossAmount: 'Amount with VAT',
  currency: 'Currency',
  paidDate: 'Paid date',
  reference: 'Variable symbol / reference',
};

const REQUIRED_HINT: Partial<Record<ImportField, string>> = {
  clientName: 'Required',
  netAmount: 'Required (or amount with VAT)',
};

const CONFIDENCE_CLASSES: Record<MatchRow['confidence'], string> = {
  exact: 'bg-green-100 text-green-800',
  probable: 'bg-blue-100 text-blue-800',
  ambiguous: 'bg-amber-100 text-amber-800',
  none: 'bg-gray-100 text-gray-700',
};

const CONFIDENCE_LABELS: Record<MatchRow['confidence'], string> = {
  exact: 'Exact',
  probable: 'Probable',
  ambiguous: 'Ambiguous',
  none: 'No match',
};

function ConfidenceBadge({ confidence }: { confidence: MatchRow['confidence'] }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${CONFIDENCE_CLASSES[confidence]}`}
    >
      {CONFIDENCE_LABELS[confidence]}
    </span>
  );
}

function invoiceLabel(invoice: ImportInvoiceView | undefined): string {
  if (!invoice) return '—';
  const period = invoice.to ? ` · ${formatDisplayDate(invoice.from)} – ${formatDisplayDate(invoice.to)}` : '';
  const customers = invoice.customerNames.length > 0 ? invoice.customerNames.join(', ') : 'No customer';
  return `#${invoice.id} ${customers}${period} · ${formatCurrency(invoice.invoicedAmount, false)}`;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export default function InvoiceImportPage() {
  const analyzeMutation = useAnalyzeImportFile();
  const matchMutation = useMatchImportFile();
  const confirmMutation = useConfirmImportPairs();

  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<AnalyzeResult | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [options, setOptions] = useState<MatchOptions>({
    amountTolerance: 1,
    amountSource: 'net',
    vatRatePercent: 21,
  });
  const [matches, setMatches] = useState<MatchResult | null>(null);
  const [confirmation, setConfirmation] = useState<ConfirmResult | null>(null);
  // MKS-349 follow-up — off every run, never remembered. See `ConfirmOptions`.
  const [markUnpaidWhenNoPaidDate, setMarkUnpaidWhenNoPaidDate] = useState(false);

  /** Row number → the invoice the operator has settled on. Absent = not pairing this row. */
  const [selection, setSelection] = useState<Record<number, number | null>>({});

  const invoiceById = useMemo(
    () => new Map((matches?.invoices ?? []).map((invoice) => [invoice.id, invoice])),
    [matches]
  );

  const unmatchedInvoices = useMemo(
    () =>
      (matches?.unmatchedInvoiceIds ?? [])
        .map((id) => invoiceById.get(id))
        .filter((invoice): invoice is ImportInvoiceView => invoice !== undefined),
    [matches, invoiceById]
  );

  // A row that already carries its number on the invoice is DONE — it is shown
  // for reassurance on a re-import, never offered for confirmation again.
  const pairable = (row: MatchRow) => !row.alreadyApplied;
  const autoMatched = (matches?.rows ?? []).filter(
    (row) => pairable(row) && (row.confidence === 'exact' || row.confidence === 'probable')
  );
  const alreadyApplied = (matches?.rows ?? []).filter((row) => row.alreadyApplied);
  const ambiguous = (matches?.rows ?? []).filter((row) => row.confidence === 'ambiguous');
  const unmatchedRows = (matches?.rows ?? []).filter(
    (row) => pairable(row) && row.confidence === 'none'
  );

  const pairs: ConfirmPair[] = useMemo(() => {
    if (!matches) return [];
    const result: ConfirmPair[] = [];
    for (const row of matches.rows) {
      if (!pairable(row)) continue;
      const invoiceId = selection[row.row];
      if (invoiceId === undefined || invoiceId === null) continue;
      // Without a number from the file there is nothing to back-fill, which is
      // the whole point of the screen.
      if (!row.invoiceNumber) continue;
      result.push({
        invoiceId,
        invoiceNumber: row.invoiceNumber,
        clientName: row.clientName,
        dueDate: row.dueDate,
        paidDate: row.paidDate,
      });
    }
    return result;
  }, [matches, selection]);

  /**
   * What confirming would write for one row, against the invoice it is paired
   * with right now. Shared by all three groups so the preview a row shows in
   * "Ambiguous" is the same one it shows once it has been paired.
   */
  const changesFor = (row: MatchRow): string => {
    const invoiceId = selection[row.row];
    if (invoiceId === undefined || invoiceId === null) return '';
    return formatChangePreview(
      describeRowChanges(row, invoiceById.get(invoiceId), { markUnpaidWhenNoPaidDate })
    );
  };

  const resetAll = () => {
    setFile(null);
    setAnalysis(null);
    setMapping({});
    setMatches(null);
    setConfirmation(null);
    setSelection({});
    setMarkUnpaidWhenNoPaidDate(false);
    analyzeMutation.reset();
    matchMutation.reset();
    confirmMutation.reset();
  };

  const handleAnalyze = () => {
    if (!file) return;
    analyzeMutation.mutate(file, {
      onSuccess: (response) => {
        setAnalysis(response.data);
        setMapping(response.data.mapping);
        setOptions(response.data.options);
        setMatches(null);
        setSelection({});
      },
    });
  };

  const handleMatch = () => {
    if (!file) return;
    matchMutation.mutate(
      { file, mapping, options },
      {
        onSuccess: (response) => {
          setMatches(response.data);
          setConfirmation(null);
          // Pre-tick every row the matcher resolved on its own. An ambiguous
          // row starts untouched: the operator chooses, nothing is guessed.
          const next: Record<number, number | null> = {};
          for (const row of response.data.rows) {
            if (!row.alreadyApplied && row.matchedInvoiceId !== null) {
              next[row.row] = row.matchedInvoiceId;
            }
          }
          setSelection(next);
        },
      }
    );
  };

  const handleConfirm = () => {
    if (pairs.length === 0) return;
    confirmMutation.mutate(
      { pairs, options: { markUnpaidWhenNoPaidDate } },
      { onSuccess: (response) => setConfirmation(response.data) }
    );
  };

  const setRowInvoice = (rowNumber: number, invoiceId: number | null) => {
    setSelection((current) => ({ ...current, [rowNumber]: invoiceId }));
  };

  const toggleAllAutoMatched = (checked: boolean) => {
    setSelection((current) => {
      const next = { ...current };
      for (const row of autoMatched) {
        next[row.row] = checked ? row.matchedInvoiceId : null;
      }
      return next;
    });
  };

  const allAutoMatchedSelected =
    autoMatched.length > 0 && autoMatched.every((row) => selection[row.row]);

  const canMatch = Boolean(file && mapping.clientName && (mapping.netAmount || mapping.grossAmount));

  return (
    <div className="p-6 max-w-7xl">
      <header className="mb-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Import invoices from accounting</h1>
            <p className="text-sm text-gray-600 mt-1">
              Upload the list of invoices your accounting system actually issued. Monksheets pairs
              each one with its own billing record by client, amount and period, and writes the
              invoice number, due date and paid date back. Nothing is written until you confirm.
            </p>
          </div>
          <Link to="/invoices" className="text-sm font-medium text-primary-600 hover:underline whitespace-nowrap">
            Back to invoices
          </Link>
        </div>
      </header>

      {analyzeMutation.isError && (
        <DismissibleErrorBanner
          message={errorMessage(analyzeMutation.error, 'The file could not be read.')}
          onDismiss={() => analyzeMutation.reset()}
        />
      )}
      {matchMutation.isError && (
        <DismissibleErrorBanner
          message={errorMessage(matchMutation.error, 'Matching failed.')}
          onDismiss={() => matchMutation.reset()}
        />
      )}
      {confirmMutation.isError && (
        <DismissibleErrorBanner
          message={errorMessage(confirmMutation.error, 'Confirming failed.')}
          onDismiss={() => confirmMutation.reset()}
        />
      )}

      {/* Step 1 — the file */}
      <section className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">1. Accounting export</h2>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            aria-label="Accounting export"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setAnalysis(null);
              setMatches(null);
              setConfirmation(null);
              setSelection({});
            }}
            className="block text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <Button onClick={handleAnalyze} disabled={!file} isLoading={analyzeMutation.isPending}>
            Read columns
          </Button>
          {analysis && (
            <span className="text-sm text-gray-600">
              {analysis.rowCount} rows, {analysis.headers.length} columns
            </span>
          )}
          {(analysis || matches) && (
            <Button variant="ghost" onClick={resetAll}>
              Start over
            </Button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          CSV or XLSX from any accounting system — there is no fixed format. The first row must be
          the column headers.
        </p>
      </section>

      {/* Step 2 — the mapping */}
      {analysis && (
        <section className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-1">2. Map the columns</h2>
          <p className="text-xs text-gray-500 mb-3">
            Pre-filled from the mapping this Monksheets used last and from the column names in your
            file. Your choices are remembered for the next import.
          </p>

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {IMPORT_FIELDS.map((field) => (
              <div key={field}>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  htmlFor={`map-${field}`}
                >
                  {FIELD_LABELS[field]}
                  {REQUIRED_HINT[field] && (
                    <span className="ml-1 text-xs font-normal text-gray-500">
                      ({REQUIRED_HINT[field]})
                    </span>
                  )}
                </label>
                <select
                  id={`map-${field}`}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  value={mapping[field] ?? ''}
                  onChange={(e) =>
                    setMapping((current) => {
                      const next = { ...current };
                      if (e.target.value === '') delete next[field];
                      else next[field] = e.target.value;
                      return next;
                    })
                  }
                >
                  <option value="">— not in this file —</option>
                  {analysis.headers.map((header, index) => (
                    <option key={`${header}-${index}`} value={header}>
                      {header === '' ? `(column ${index + 1})` : header}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-3 border-t border-gray-200 pt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="map-tolerance">
                Amount tolerance
              </label>
              <input
                id="map-tolerance"
                type="number"
                min={0}
                step="0.01"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                value={options.amountTolerance}
                onChange={(e) =>
                  setOptions((current) => ({
                    ...current,
                    amountTolerance: Number(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="map-source">
                Compare against
              </label>
              <select
                id="map-source"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                value={options.amountSource}
                onChange={(e) =>
                  setOptions((current) => ({
                    ...current,
                    amountSource: e.target.value === 'gross' ? 'gross' : 'net',
                  }))
                }
              >
                <option value="net">Amount without VAT</option>
                <option value="gross">Amount with VAT ÷ VAT rate</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="map-vat">
                VAT rate (%)
              </label>
              <input
                id="map-vat"
                type="number"
                min={0}
                max={100}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm disabled:bg-gray-100"
                disabled={options.amountSource !== 'gross'}
                value={options.vatRatePercent}
                onChange={(e) =>
                  setOptions((current) => ({
                    ...current,
                    vatRatePercent: Number(e.target.value) || 0,
                  }))
                }
              />
            </div>
          </div>

          {analysis.sampleRows.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                First rows in your file
              </p>
              <table className="min-w-full text-xs border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {analysis.headers.map((header, index) => (
                      <th key={`${header}-${index}`} className="px-2 py-1 text-left font-medium text-gray-600">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {analysis.sampleRows.map((sample, rowIndex) => (
                    <tr key={rowIndex} className="border-t border-gray-100">
                      {analysis.headers.map((_header, index) => (
                        <td key={index} className="px-2 py-1 text-gray-700 whitespace-nowrap">
                          {sample[index] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end mt-4">
            <Button onClick={handleMatch} disabled={!canMatch} isLoading={matchMutation.isPending}>
              Match against Monksheets
            </Button>
          </div>
          {!canMatch && (
            <p className="text-xs text-amber-700 mt-2 text-right">
              Map the client name and at least one amount column to continue.
            </p>
          )}
        </section>
      )}

      {/* Step 3 — review */}
      {matches && (
        <section className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-gray-900 mb-2">3. Review</h2>
            <dl className="flex flex-wrap gap-6 text-sm">
              <div>
                <dt className="text-gray-500">Rows</dt>
                <dd className="font-semibold text-gray-900">{matches.totalRows}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Exact</dt>
                <dd className="font-semibold text-green-700">{matches.counts.exact}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Probable</dt>
                <dd className="font-semibold text-blue-700">{matches.counts.probable}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Ambiguous</dt>
                <dd className="font-semibold text-amber-700">{matches.counts.ambiguous}</dd>
              </div>
              <div>
                <dt className="text-gray-500">No match</dt>
                <dd className="font-semibold text-gray-700">{matches.counts.none}</dd>
              </div>
            </dl>

            {/* MKS-349 follow-up — the rollout Petra runs in production is
                "mark everything paid, then import the list of unpaid
                invoices". For THAT file an empty paid-date column means "not
                paid"; for every other file it means "the export does not carry
                this". Off by default and reset on "Start over", so it is never
                on by accident. */}
            <label className="mt-4 flex items-start gap-2 text-sm text-gray-700 border-t border-gray-200 pt-3">
              <input
                type="checkbox"
                checked={markUnpaidWhenNoPaidDate}
                onChange={(e) => setMarkUnpaidWhenNoPaidDate(e.target.checked)}
                className="mt-0.5 rounded border-gray-300"
              />
              <span>
                Rows without a paid date mark the invoice as unpaid
                <span className="block text-xs text-gray-500">
                  Use this when the file is the accounting system&apos;s list of unpaid invoices.
                  Leave it off and a missing paid date changes nothing.
                </span>
              </span>
            </label>
          </div>

          {alreadyApplied.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
              <strong>{alreadyApplied.length}</strong> row
              {alreadyApplied.length === 1 ? ' is' : 's are'} already recorded in Monksheets — their
              invoice numbers are on the matching invoices. Re-importing the same file changes
              nothing.
            </div>
          )}

          {/* Matched */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">
                Matched ({autoMatched.length})
              </h3>
              {autoMatched.length > 0 && (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    aria-label="Select all matched rows"
                    checked={allAutoMatchedSelected}
                    onChange={(e) => toggleAllAutoMatched(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Select all
                </label>
              )}
            </div>
            {autoMatched.length === 0 ? (
              <p className="text-sm text-gray-500">Nothing matched automatically.</p>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-1 pr-3 w-8" />
                    <th className="py-1 pr-3">Row</th>
                    <th className="py-1 pr-3">Invoice no.</th>
                    <th className="py-1 pr-3">Client</th>
                    <th className="py-1 pr-3">Amount</th>
                    <th className="py-1 pr-3">Issued</th>
                    <th className="py-1 pr-3">Monksheets invoice</th>
                    <th className="py-1 pr-3">Will change</th>
                    <th className="py-1">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {autoMatched.map((row) => (
                    <tr key={row.row} className="border-b border-gray-100">
                      <td className="py-1 pr-3">
                        <input
                          type="checkbox"
                          aria-label={`Confirm row ${row.row}`}
                          checked={Boolean(selection[row.row])}
                          onChange={(e) =>
                            setRowInvoice(row.row, e.target.checked ? row.matchedInvoiceId : null)
                          }
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="py-1 pr-3 text-gray-500">{row.row}</td>
                      <td className="py-1 pr-3 font-medium text-gray-900">
                        {row.invoiceNumber ?? <span className="text-red-600">missing</span>}
                      </td>
                      <td className="py-1 pr-3 text-gray-700">{row.clientName}</td>
                      <td className="py-1 pr-3 text-gray-700">
                        {row.netAmount === null ? '—' : formatCurrency(row.netAmount)}
                      </td>
                      <td className="py-1 pr-3 text-gray-700">{formatDisplayDate(row.issueDate)}</td>
                      <td className="py-1 pr-3 text-gray-700">
                        {invoiceLabel(invoiceById.get(row.matchedInvoiceId ?? -1))}
                      </td>
                      <td className="py-1 pr-3 text-gray-600 whitespace-nowrap">
                        {changesFor(row) || '—'}
                      </td>
                      <td className="py-1">
                        <ConfidenceBadge confidence={row.confidence} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Ambiguous */}
          {ambiguous.length > 0 && (
            <div className="bg-white border border-amber-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                Ambiguous ({ambiguous.length})
              </h3>
              <p className="text-xs text-gray-500 mb-2">
                More than one Monksheets invoice fits this client and amount. Pick the right one, or
                leave it unpaired.
              </p>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-1 pr-3">Row</th>
                    <th className="py-1 pr-3">Invoice no.</th>
                    <th className="py-1 pr-3">Client</th>
                    <th className="py-1 pr-3">Amount</th>
                    <th className="py-1 pr-3">Pair with</th>
                    <th className="py-1">Will change</th>
                  </tr>
                </thead>
                <tbody>
                  {ambiguous.map((row) => (
                    <tr key={row.row} className="border-b border-gray-100">
                      <td className="py-1 pr-3 text-gray-500">{row.row}</td>
                      <td className="py-1 pr-3 font-medium text-gray-900">{row.invoiceNumber}</td>
                      <td className="py-1 pr-3 text-gray-700">{row.clientName}</td>
                      <td className="py-1 pr-3 text-gray-700">
                        {row.netAmount === null ? '—' : formatCurrency(row.netAmount)}
                      </td>
                      <td className="py-1 pr-3">
                        <select
                          aria-label={`Pair row ${row.row}`}
                          className="w-full rounded-md border-gray-300 shadow-sm text-sm"
                          value={selection[row.row] ?? ''}
                          onChange={(e) =>
                            setRowInvoice(row.row, e.target.value === '' ? null : Number(e.target.value))
                          }
                        >
                          <option value="">— leave unpaired —</option>
                          {row.candidates.map((candidate) => (
                            <option key={candidate.invoiceId} value={candidate.invoiceId}>
                              {invoiceLabel(invoiceById.get(candidate.invoiceId))}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-1 text-gray-600 whitespace-nowrap">
                        {changesFor(row) || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Unmatched, side by side */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                Unmatched rows from the file ({unmatchedRows.length})
              </h3>
              <p className="text-xs text-gray-500 mb-2">
                Pair one by hand with any Monksheets invoice that is still open.
              </p>
              {unmatchedRows.length === 0 ? (
                <p className="text-sm text-gray-500">Every row found an invoice.</p>
              ) : (
                <ul className="space-y-3">
                  {unmatchedRows.map((row) => (
                    <li key={row.row} className="border-b border-gray-100 pb-3 last:border-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-medium text-gray-900">
                          {row.invoiceNumber ?? `Row ${row.row}`}
                        </span>
                        <span className="text-sm text-gray-700">
                          {row.netAmount === null ? '—' : formatCurrency(row.netAmount)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {row.clientName ?? 'No client name'} · {formatDisplayDate(row.issueDate)}
                      </div>
                      {row.problem && <div className="text-xs text-gray-500 mt-0.5">{row.problem}</div>}
                      <select
                        aria-label={`Pair row ${row.row}`}
                        className="mt-1 w-full rounded-md border-gray-300 shadow-sm text-sm"
                        value={selection[row.row] ?? ''}
                        onChange={(e) =>
                          setRowInvoice(row.row, e.target.value === '' ? null : Number(e.target.value))
                        }
                        disabled={!row.invoiceNumber}
                      >
                        <option value="">— leave unpaired —</option>
                        {unmatchedInvoices.map((invoice) => (
                          <option key={invoice.id} value={invoice.id}>
                            {invoiceLabel(invoice)}
                          </option>
                        ))}
                      </select>
                      {changesFor(row) && (
                        <p className="text-xs text-gray-600 mt-0.5">{changesFor(row)}</p>
                      )}
                      {!row.invoiceNumber && (
                        <p className="text-xs text-amber-700 mt-0.5">
                          This row has no invoice number, so there is nothing to write back.
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                Monksheets invoices nothing claimed ({unmatchedInvoices.length})
              </h3>
              <p className="text-xs text-gray-500 mb-2">
                Invoices with no number yet that no row in the file matched.
              </p>
              {unmatchedInvoices.length === 0 ? (
                <p className="text-sm text-gray-500">Every open invoice was claimed by a row.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {unmatchedInvoices.map((invoice) => (
                    <li key={invoice.id} className="flex items-baseline justify-between gap-2 border-b border-gray-100 py-1 last:border-0">
                      <Link
                        to={`/invoices/${invoice.id}`}
                        className="text-primary-600 hover:underline"
                      >
                        {invoice.customerNames.join(', ') || invoice.name || `Invoice #${invoice.id}`}
                      </Link>
                      <span className="text-gray-700 whitespace-nowrap">
                        {formatDisplayDate(invoice.to)} · {formatCurrency(invoice.invoicedAmount, false)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Step 4 — confirm */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between gap-4">
            <p className="text-sm text-gray-700">
              <strong>{pairs.length}</strong> invoice{pairs.length === 1 ? '' : 's'} will get their
              number, due date and paid date from the file
              {markUnpaidWhenNoPaidDate
                ? ', and a row with no paid date will mark its invoice unpaid'
                : ''}
              . Every change is recorded in the audit trail.
            </p>
            <Button
              onClick={handleConfirm}
              disabled={pairs.length === 0}
              isLoading={confirmMutation.isPending}
            >
              Confirm {pairs.length > 0 ? pairs.length : ''}
            </Button>
          </div>
        </section>
      )}

      {/* Result */}
      {confirmation && (
        <section className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
          <h2 className="text-sm font-semibold text-green-900 mb-2">Done</h2>
          <p className="text-sm text-green-900">
            {confirmation.applied} invoice{confirmation.applied === 1 ? '' : 's'} updated,{' '}
            {confirmation.unchanged} already up to date, {confirmation.failed} not applied.
          </p>
          {confirmation.failed > 0 && (
            <ul className="mt-2 text-sm text-red-800 list-disc list-inside">
              {confirmation.results
                .filter((result) => result.outcome === 'conflict' || result.outcome === 'not-found')
                .map((result) => (
                  <li key={`${result.invoiceId}-${result.invoiceNumber}`}>
                    {result.invoiceNumber}: {result.detail ?? result.outcome}
                  </li>
                ))}
            </ul>
          )}
          <div className="mt-3 flex gap-3">
            <Link to="/invoices">
              <Button variant="secondary">Go to invoices</Button>
            </Link>
            <Button variant="ghost" onClick={resetAll}>
              Import another file
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
